import { BrowserAgent } from '@newrelic/browser-agent/loaders/browser-agent'
import { AgentOptions } from '@newrelic/browser-agent/loaders/agent'

const options: AgentOptions = {
  init: {
    session_replay: {
      enabled: true,
      block_selector: '',
      mask_text_selector: '*',
      sampling_rate: 100.0,
      error_sampling_rate: 100.0,
      mask_all_inputs: true,
      collect_fonts: true,
      inline_images: false,
      inline_stylesheet: true, // rrweb option; not yet in @newrelic/browser-agent types
      fix_stylesheets: true,
      preload: false,
      mask_input_options: {},
    } as AgentOptions['init'] extends { session_replay?: infer R } ? R : never,
    distributed_tracing: { enabled: true },
    performance: { capture_measures: true },
    browser_consent_mode: { enabled: false },
    privacy: { cookies_enabled: true },
    ajax: {
      deny_list: ['bam.nr-data.net'],
      capture_payloads: 'none',
    },
    api: { register: { enabled: true, duplicate_data_to_container: false } },
  },
  info: {
    beacon: 'bam.nr-data.net',
    errorBeacon: 'bam.nr-data.net',
    licenseKey: import.meta.env.VITE_NR_LICENSE_KEY as string,
    applicationID: import.meta.env.VITE_NR_APPLICATION_ID as string,
    sa: 1,
  },
  loader_config: {
    accountID: import.meta.env.VITE_NR_ACCOUNT_ID as string,
    trustKey: import.meta.env.VITE_NR_TRUST_KEY as string,
    agentID: import.meta.env.VITE_NR_AGENT_ID as string,
    licenseKey: import.meta.env.VITE_NR_LICENSE_KEY as string,
    applicationID: import.meta.env.VITE_NR_APPLICATION_ID as string,
  },
}

export const newRelic = new BrowserAgent(options)

// console.error/warn -> New Relic Logs. The agent does not collect console
// output on its own, so the auth-failure console.error calls were only ever
// visible in the user's own devtools.
newRelic.wrapLogger(console, 'error', { level: 'ERROR' })
newRelic.wrapLogger(console, 'warn', { level: 'WARN' })

// Page Action Naming
export const NEW_RELIC = {
  PAGE_ACTION: {
    clickCreateIdea: 'clickedCreateIdea',
    changeLanguageEn: 'changedLanguageEn',
    changeLanguageZh: 'changedLanguageZh',
    changeLanguageCn: 'changedLanguageCn',
    changeLanguageJp: 'changedLanguageJp',
    createIdeaEmailedSupport: 'createIdeaEmailedSupport',
  },
  FORM_ACTION: {
    submitCreateIdea: 'submitIdea',
  },
  LIST_CUSTOMIZE_ACTION: {
    columnChange: 'columnChange',
    columnResize: 'columnResize',
    sortChange: 'sortChange',
    resetColumn: 'resetColumn',
  },
  ERROR: {
    auth0: 'auth0Error',
    ideaDetail: 'ideaDetailError',
  },
  AUTH: {
    auth0Token: 'auth0TokenStep',
    exchangeToken: 'exchangeTokenStep',
    verifyToken: 'verifyTokenStep',
    silentRefresh: 'silentRefreshStep',
  },
  CUSTOM_EVENT: {
    // Fired when the user actively switches the mobile idea list view.
    ideaListMobileViewChanged: 'IdeaListMobileViewChanged',
    // Fired once per mount when the mobile idea list is shown, capturing the
    // view actually in use (including users who never switch from the default).
    ideaListMobileViewShown: 'IdeaListMobileViewShown',
  },
}

export const NEW_RELIC_AUTH_EVENT = {
  auth0TokenAcquired: 'Auth0TokenAcquired',
  auth0TokenFailed: 'Auth0TokenFailed',
  exchangeTokenSucceeded: 'ExchangeTokenSucceeded',
  exchangeTokenFailed: 'ExchangeTokenFailed',
  verifyTokenSucceeded: 'VerifyTokenSucceeded',
  verifyTokenFailed: 'VerifyTokenFailed',
  silentRefreshRequested: 'SilentAuth0RefreshRequested',
  silentRefreshSucceeded: 'SilentAuth0RefreshSucceeded',
  silentRefreshFailed: 'SilentAuth0RefreshFailed',
} as const
